import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Button,
	TextField,
	Divider,
	Stack,
} from '@mui/material';
import { API_URL } from '../Config';
import { toast } from 'sonner';

const Day_Close = () => {
	const [data, setData] = useState(null);
	const [openingBal, setOpeningBal] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [cashHandover, setCashHandover] = useState('');
	const user_id = localStorage.getItem('userId');

	const fetchData = async () => {
		try {
			const formData = new FormData();
			formData.append('user_id', user_id);

			const response = await fetch(`${API_URL}/get_total_pos_sales`, {
				method: 'POST',
				headers: {
					'authorization': `Bearer ${localStorage.getItem('accessToken')}`,
				},
				body: formData,
			});

			if (response.ok) {
				const result = await response.json();
				setData(result.data);
				setOpeningBal(result.opening_bal);
			} else {
				toast.error('Failed to fetch data');
			}
		} catch (error) {
			toast.error('An error occurred while fetching data');
		}
	};

	useEffect(() => {
		if (isModalOpen) {
			fetchData();
		}
	}, [isModalOpen]);

	useEffect(() => {
		const handleModalOpen = () => {
			const modalElement = document.getElementById('Day-Close');
			if (modalElement) {
				modalElement.addEventListener('show.bs.modal', () => {
					setIsModalOpen(true);
				});
				modalElement.addEventListener('hide.bs.modal', () => {
					setIsModalOpen(false);
				});
			}
		};

		handleModalOpen();

		return () => {
			const modalElement = document.getElementById('Day-Close');
			if (modalElement) {
				modalElement.removeEventListener('show.bs.modal', () => {});
				modalElement.removeEventListener('hide.bs.modal', () => {});
			}
		};
	}, []);

	const calculateTotal = (paymentMode) => {
		if (!data) return 0;
		return data
			.filter(item => item.payment_mode.toLowerCase() === paymentMode.toLowerCase())
			.reduce((sum, item) => sum + parseFloat(item.amount), 0);
	};

	const totalCash = calculateTotal('cash');
	const totalCheque = calculateTotal('cheque');
	const totalCard = calculateTotal('card');
	const totalSale = totalCash + totalCheque + totalCard;
	const openingBalAmount = openingBal?.total || 0;
	const netBalance = totalSale - openingBalAmount;

	const formatCurrency = (value) => {
		const symbol = localStorage.getItem("currencySymbol") || '£';
		return `${symbol}${parseFloat(value || 0).toFixed(2)}`;
	};

	const handleSubmit = async () => {
		if (!cashHandover || parseFloat(cashHandover) < 0) {
			toast.warning('Please enter a valid cash handover amount');
			return;
		}

		try {
			const formData = new FormData();
			formData.append('user_id', user_id);
			formData.append('cash_handover', cashHandover);

			const response = await fetch(`${API_URL}/ws-submit-day-close`, {
				method: 'POST',
				headers: {
					'authorization': `Bearer ${localStorage.getItem('accessToken')}`,
				},
				body: formData,
			});

			if (response.ok) {
				const result = await response.json();
				if (result.success) {
					toast.success('Day Close Submitted Successfully');
					setIsModalOpen(false);
					setCashHandover('');
					
					const modalElement = document.getElementById('Day-Close');
					const bsModal = window.bootstrap.Modal.getInstance(modalElement);
					if (bsModal) {
						bsModal.hide();
					}
					
					setTimeout(() => {
						window.location.reload();
					}, 1500);
				} else {
					toast.error(result.message || 'Failed to submit');
				}
			} else {
				toast.error('Failed to submit Day Close');
			}
		} catch (error) {
			toast.error('An error occurred while submitting data');
		}
	};

	return (
		<div
			className="modal fade"
			id="Day-Close"
			tabIndex="-1"
			aria-hidden="true"
		>
			<div className="modal-dialog modal-dialog-centered">
				<div className="modal-content" style={{ borderRadius: '8px', border: 'none' }}>
					<Box sx={{ p: 3 }}>
						<Typography
							variant="h6"
							sx={{
								fontWeight: 600,
								fontSize: "1.125rem",
								color: "#212b36",
								mb: 2
							}}
						>
							Day Close
						</Typography>

						<Stack spacing={2.5} sx={{ mb: 3 }}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									p: 2,
									backgroundColor: '#f5f7fa',
									borderRadius: 1.5,
								}}
							>
								<Typography sx={{ fontSize: '0.875rem', color: '#637381', fontWeight: 500 }}>
									Opening Balance:
								</Typography>
								<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 600 }}>
									{formatCurrency(openingBalAmount)}
								</Typography>
							</Box>

							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									p: 2,
									backgroundColor: '#f5f7fa',
									borderRadius: 1.5,
								}}
							>
								<Typography sx={{ fontSize: '0.875rem', color: '#637381', fontWeight: 500 }}>
									Total Sale:
								</Typography>
								<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 600 }}>
									{formatCurrency(totalSale)}
								</Typography>
							</Box>

							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									p: 2,
									backgroundColor: '#e3f2fd',
									borderRadius: 1.5,
									border: '1px solid #1976d2',
								}}
							>
								<Typography sx={{ fontSize: '0.875rem', color: '#1976d2', fontWeight: 600 }}>
									Net Balance:
								</Typography>
								<Typography sx={{ fontSize: '0.875rem', color: '#1976d2', fontWeight: 700 }}>
									{formatCurrency(netBalance)}
								</Typography>
							</Box>

							<Divider sx={{ my: 1 }} />

							<Stack spacing={1.5}>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
									<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Cash:</Typography>
									<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
										{formatCurrency(totalCash)}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
									<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Cheque:</Typography>
									<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
										{formatCurrency(totalCheque)}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
									<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Card:</Typography>
									<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
										{formatCurrency(totalCard)}
									</Typography>
								</Box>
							</Stack>

							<TextField
								label="Cash Handover"
								className="form-input-border"
								type="number"
								fullWidth
								value={cashHandover}
								onChange={(e) => setCashHandover(e.target.value)}
								placeholder="Enter cash handover amount"
								sx={{
									mt: 2,
									"& .MuiOutlinedInput-root": {
										backgroundColor: "#f5f7fa",
									}
								}}
							/>
						</Stack>

						<Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
							<Button
								data-bs-dismiss="modal"
								className="button-variant-outlined"
								variant="outlined"
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								className="button-variant-contained"
							>
								Submit
							</Button>
						</Box>
					</Box>
				</div>
			</div>
		</div>
	);
};

export default Day_Close;
